// frontend/src/screens/admin/Dashboard/PreShiftAnalyticsDashboard.jsx
import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Divider,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearIcon from '@mui/icons-material/Clear';
import AssessmentIcon from '@mui/icons-material/Assessment';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import GroupIcon from '@mui/icons-material/Group';
import BusinessIcon from '@mui/icons-material/Business';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { BarChart } from '@mui/x-charts/BarChart';
import { PieChart } from '@mui/x-charts/PieChart';
import { LineChart } from '@mui/x-charts/LineChart';
import { useLayout } from '../../../context/LayoutContext';

import { useGetPreShiftAnalyticsQuery } from '../../../slices/preShiftApiSlice';
import { useGetHospitalLocationsQuery } from '../../../slices/hospitalLocationApiSlice';

// ─── Palette (matches SchedulingDashboard) ─────────────────────────────────
const NAVY   = '#0d1b2a';
const TEAL   = '#0a8577';
const TEAL_L = '#0cb89e';
const AMBER  = '#e8a020';
const RED    = '#c0392b';
const GREEN  = '#27ae60';
const SLATE  = '#1e2d3d';
const MUTED  = '#8fa3b1';
const WHITE  = '#f0f4f8';
const BLUE   = '#2196f3';
const PURPLE = '#8e44ad';

const OUTCOME_COLORS = { approved: GREEN, rejected: RED, pending: AMBER, total: TEAL_L };

const CATEGORY_PALETTE = [TEAL_L, GREEN, AMBER, PURPLE, BLUE, RED, '#4527a0', '#00695c', '#ad1457', MUTED];

const FONT = '"DM Mono", monospace';

// ─── Helpers ──────────────────────────────────────────────────────────────
const pct = (n, d) => (d === 0 ? 0 : Math.round((n / d) * 100));

const approvalRateOf = (row) => {
  const decided = (row.approved || 0) + (row.rejected || 0);
  if (decided === 0) return null;
  return Math.round((row.approved / decided) * 100);
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 48) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
};

const monthLabel = (yyyymm) => {
  if (!yyyymm) return '—';
  const [y, m] = yyyymm.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('en-GB', { month: 'short', year: '2-digit' });
};

// Overrides so @mui/x-charts axis/legend/grid text reads correctly against
// the dark SLATE background instead of the library's default dark-on-light.
const chartDarkSx = {
  '& .MuiChartsAxis-tickLabel': { fill: `${MUTED} !important`, fontFamily: FONT, fontSize: '0.65rem' },
  '& .MuiChartsAxis-line': { stroke: `${TEAL}55` },
  '& .MuiChartsAxis-tick': { stroke: `${TEAL}55` },
  '& .MuiChartsLegend-series text': { fill: `${WHITE} !important`, fontFamily: FONT, fontSize: '0.7rem' },
  '& .MuiChartsGrid-line': { stroke: `${TEAL}18` },
  '& .MuiChartsTooltip-table': { fontFamily: FONT },
};

// ─── Shared sub-components (mirrors SchedulingDashboard) ──────────────────

const SectionTitle = ({ icon: Icon, label }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, mt: 1 }}>
    <Box sx={{ width: 4, height: 28, bgcolor: TEAL, borderRadius: 1 }} />
    <Icon sx={{ color: TEAL, fontSize: 22 }} />
    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: '0.85rem', letterSpacing: 2, textTransform: 'uppercase', color: WHITE }}>
      {label}
    </Typography>
  </Box>
);

const StatCard = ({ label, value, sub, color = TEAL, icon: Icon }) => (
  <Paper elevation={0} sx={{
    bgcolor: SLATE, border: `1px solid ${color}22`, borderRadius: 2,
    p: 2.5, position: 'relative', overflow: 'hidden', height: '100%',
    '&::before': { content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3, bgcolor: color },
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 8px 24px ${color}22` },
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
      <Box>
        <Typography sx={{ fontSize: '0.7rem', fontFamily: FONT, letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, mb: 1 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '2rem', fontFamily: FONT, fontWeight: 700, color: WHITE, lineHeight: 1 }}>
          {value}
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.75, fontFamily: FONT }}>
            {sub}
          </Typography>
        )}
      </Box>
      {Icon && <Icon sx={{ color: `${color}99`, fontSize: 28 }} />}
    </Box>
  </Paper>
);

const BarRow = ({ label, value, total, color, showPct = true }) => {
  const p = pct(value, total);
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography sx={{ fontSize: '0.75rem', color: WHITE, fontFamily: FONT, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: WHITE, fontFamily: FONT }}>{value}</Typography>
          {showPct && <Typography sx={{ fontSize: '0.7rem', color: MUTED, fontFamily: FONT, minWidth: 36, textAlign: 'right' }}>{p}%</Typography>}
        </Box>
      </Box>
      <Box sx={{ height: 6, bgcolor: `${color}22`, borderRadius: 1, overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${p}%`, bgcolor: color, borderRadius: 1, transition: 'width 0.6s ease' }} />
      </Box>
    </Box>
  );
};

const GaugeRing = ({ value, label, color = TEAL, size = 100 }) => {
  const v = value === null || value === undefined ? 0 : value;
  const r = 40;
  const circ = 2 * Math.PI * r;
  const dash = (v / 100) * circ;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke={`${color}22`} strokeWidth="10" />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
          transform="rotate(-90 50 50)" style={{ transition: 'stroke-dasharray 0.8s ease' }} />
        <text x="50" y="54" textAnchor="middle" fill={WHITE}
          style={{ fontSize: 18, fontFamily: FONT, fontWeight: 700 }}>
          {value === null ? '—' : `${value}%`}
        </text>
      </svg>
      <Typography sx={{ fontSize: '0.68rem', color: MUTED, fontFamily: FONT, textAlign: 'center', letterSpacing: 1, textTransform: 'uppercase' }}>
        {label}
      </Typography>
    </Box>
  );
};

const FilterSelect = ({ label, value, onChange, options, disabled }) => (
  <FormControl size="small" sx={{ minWidth: 160,
    '& .MuiOutlinedInput-root': { bgcolor: SLATE, color: WHITE, borderRadius: 1.5,
      '& fieldset': { borderColor: `${TEAL}44` },
      '&:hover fieldset': { borderColor: TEAL },
      '&.Mui-focused fieldset': { borderColor: TEAL },
    },
    '& .MuiInputLabel-root': { color: MUTED },
    '& .MuiSvgIcon-root': { color: MUTED },
  }}>
    <InputLabel sx={{ color: MUTED, fontSize: '0.8rem' }}>{label}</InputLabel>
    <Select value={value} label={label} onChange={e => onChange(e.target.value)} disabled={disabled}
      sx={{ fontSize: '0.8rem', fontFamily: FONT }}
      MenuProps={{
        PaperProps: {
          sx: {
            bgcolor: SLATE,
            border: `1px solid ${TEAL}44`,
            '& .MuiMenuItem-root': {
              color: WHITE,
              fontFamily: FONT,
              fontSize: '0.8rem',
              '&:hover': { bgcolor: `${TEAL}44`, color: WHITE },
              '&.Mui-selected': {
                bgcolor: `${TEAL}33`, color: TEAL_L,
                '&:hover': { bgcolor: `${TEAL}55` },
              },
            },
          },
        },
      }}
    >
      <MenuItem value=""><em style={{ color: MUTED }}>All</em></MenuItem>
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);

// Dark-themed leaderboard table (requester / approver / ward)
// Light-gray palette, scoped to the Leaderboards tables only
const LT_BG = '#f4f5f7';
const LT_TEXT = '#1c2530';
const LT_MUTED = '#5b6672';
const LT_BORDER = '#d7dbe0';

const BreakdownTable = ({ title, rows, extraColumns = [], emptyText }) => (
  <Paper elevation={0} sx={{ bgcolor: LT_BG, border: `1px solid ${LT_BORDER}`, borderRadius: 2, p: 2.5, height: '100%' }}>
    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: LT_MUTED, fontFamily: FONT, mb: 2 }}>
      {title}
    </Typography>
    {rows.length === 0 ? (
      <Typography sx={{ color: LT_MUTED, fontSize: '0.75rem', fontFamily: FONT }}>
        {emptyText || 'No data for the current filters.'}
      </Typography>
    ) : (
      <TableContainer sx={{ maxHeight: 340 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['Name', 'Total', 'Appr.', 'Rej.', 'Pend.', 'Rate', ...extraColumns.map(c => c.header)].map((h) => (
                <TableCell key={h} align={h === 'Name' ? 'left' : 'right'} sx={{
                  bgcolor: LT_BG, color: LT_MUTED, fontFamily: FONT,
                  fontSize: '0.62rem', letterSpacing: 1, textTransform: 'uppercase',
                  borderBottom: `1px solid ${LT_BORDER}`,
                }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const rate = approvalRateOf(row);
              return (
                <TableRow key={row._id || row.name}>
                  <TableCell sx={{ color: LT_TEXT, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}`, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {row._id || 'Unknown'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: LT_TEXT, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>{row.count}</TableCell>
                  <TableCell align="right" sx={{ color: GREEN, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>{row.approved || 0}</TableCell>
                  <TableCell align="right" sx={{ color: RED, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>{row.rejected || 0}</TableCell>
                  <TableCell align="right" sx={{ color: AMBER, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>{row.pending || 0}</TableCell>
                  <TableCell align="right" sx={{ color: LT_MUTED, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>{rate === null ? '—' : `${rate}%`}</TableCell>
                  {extraColumns.map((c) => (
                    <TableCell key={c.key} align="right" sx={{ color: LT_MUTED, fontFamily: FONT, fontSize: '0.7rem', borderBottom: `1px solid ${LT_BORDER}` }}>
                      {c.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Paper>
);

// Stacked Approved/Rejected/Pending bar chart, dark-themed
const StackedOutcomeBarChart = ({ rows, height = 320 }) => {
  if (!rows.length) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height }}>
        <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: FONT }}>No data for the current filters.</Typography>
      </Box>
    );
  }
  const labels = rows.map((r) => r._id || 'Unknown');
  return (
    <BarChart
      height={height}
      layout="horizontal"
      yAxis={[{ scaleType: 'band', data: labels }]}
      xAxis={[{ min: 0 }]}
      series={[
        { data: rows.map((r) => r.approved || 0), label: 'Approved', color: OUTCOME_COLORS.approved, stack: 'total' },
        { data: rows.map((r) => r.rejected || 0), label: 'Rejected', color: OUTCOME_COLORS.rejected, stack: 'total' },
        { data: rows.map((r) => r.pending || 0),  label: 'Pending',  color: OUTCOME_COLORS.pending,  stack: 'total' },
      ]}
      margin={{ left: 150, right: 20, top: 56, bottom: 30 }}
      sx={chartDarkSx}
      slotProps={{ legend: { hidden: false, position: { vertical: 'top', horizontal: 'middle' }, direction: 'row' } }}
    />
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

const PreShiftAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const { userInfo } = useSelector(s => s.auth);
  const { setFluid } = useLayout();

  React.useEffect(() => {
    setFluid(true);
    return () => setFluid(false);
  }, [setFluid]);

  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth  = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().split('T')[0];

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [fDirectorate, setFDirectorate] = useState('');
  const [fWard, setFWard] = useState('');

  const { data: hospitalLocations = [] } = useGetHospitalLocationsQuery();

  const directorates = useMemo(
    () => [...new Set(hospitalLocations.map(l => l.directorate).filter(Boolean))].sort(),
    [hospitalLocations]
  );
  const wards = useMemo(() => {
    const base = fDirectorate ? hospitalLocations.filter(l => l.directorate === fDirectorate) : hospitalLocations;
    return [...new Set(base.map(l => l.wardDepartmentArea).filter(Boolean))].sort();
  }, [hospitalLocations, fDirectorate]);

  const { data, isLoading, isFetching, isError, error, refetch } = useGetPreShiftAnalyticsQuery({
    startDate: dateFrom || undefined,
    endDate: dateTo || undefined,
    directorate: fDirectorate || undefined,
    ward: fWard || undefined,
  });

  const clearAllFilters = () => {
    setDateFrom('');
    setDateTo('');
    setFDirectorate('');
    setFWard('');
  };
  const hasFilters = dateFrom || dateTo || fDirectorate || fWard;

  const trend = data?.trend || [];
  const trendLabels = useMemo(() => trend.map((t) => monthLabel(t._id)), [trend]);

  const categoryPieData = useMemo(() => (data?.byCategory || []).map((r, i) => ({
    id: r._id || 'Unknown', value: r.count, label: r._id || 'Unknown',
    color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
  })), [data]);

  const specialsPieData = useMemo(() => (data?.bySpecialsReason || []).map((r, i) => ({
    id: r._id || 'Unknown', value: r.count, label: r._id || 'Unknown',
    color: CATEGORY_PALETTE[i % CATEGORY_PALETTE.length],
  })), [data]);

  const summary = data?.summary || { total: 0, approved: 0, rejected: 0, pending: 0, approvalRate: null, avgApprovalTimeHours: null };

  // ── Print handler ──────────────────────────────────────────────────────────
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const filterSummary = [
      dateFrom && dateTo ? `${dateFrom} to ${dateTo}` : dateFrom ? `From ${dateFrom}` : dateTo ? `To ${dateTo}` : '',
      fDirectorate ? `Directorate: ${fDirectorate}` : '',
      fWard        ? `Ward: ${fWard}` : '',
    ].filter(Boolean).join(' · ') || 'No filters applied';

    const statBox = (label, value, sub, color) => `
      <div style="background:#f2f2f2;border:1px solid #d0d0d0;border-top:3px solid ${color};border-radius:8px;padding:14px 16px;min-width:120px;flex:1;">
        <div style="font-size:9px;letter-spacing:1.5px;text-transform:uppercase;color:#555;margin-bottom:6px;font-family:'Courier New',monospace;">${label}</div>
        <div style="font-size:24px;font-weight:700;color:#111;line-height:1;font-family:'Courier New',monospace;">${value}</div>
        ${sub ? `<div style="font-size:9px;color:#555;margin-top:4px;font-family:'Courier New',monospace;">${sub}</div>` : ''}
      </div>`;

    const sectionTitle = (label) => `
      <div style="display:flex;align-items:center;gap:10px;margin:28px 0 14px;">
        <div style="width:4px;height:20px;background:#0a8577;border-radius:2px;flex-shrink:0;"></div>
        <div style="font-size:9px;font-weight:700;letter-spacing:2.5px;text-transform:uppercase;color:#0d1b2a;font-family:'Courier New',monospace;">${label}</div>
        <div style="flex:1;height:1px;background:#e0e7ef;"></div>
      </div>`;

    const bar = (value, total, color) => {
      const p = total === 0 ? 0 : Math.round((value / total) * 100);
      return `<div style="height:7px;background:#e8ecf0;border-radius:4px;overflow:hidden;margin-top:3px;">
        <div style="height:100%;width:${p}%;background:${color};border-radius:4px;"></div>
      </div>`;
    };

    const barRow = (label, value, total, color) => {
      const p = total === 0 ? 0 : Math.round((value / total) * 100);
      return `<div style="margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:3px;">
          <span style="font-size:10px;color:#0d1b2a;font-family:'Courier New',monospace;">${label}</span>
          <span style="font-size:10px;font-weight:700;color:#0d1b2a;font-family:'Courier New',monospace;">${value} <span style="color:#8fa3b1;font-weight:400;">(${p}%)</span></span>
        </div>
        ${bar(value, total, color)}
      </div>`;
    };

    // Breakdown table used for By Reason / By Directorate / Leaderboards
    const breakdownTable = (rows, nameHeader, extraCols = []) => {
      if (!rows.length) return '<div style="font-size:10px;color:#8fa3b1;">No data for the current filters.</div>';
      const headers = [nameHeader, 'Total', 'Appr.', 'Rej.', 'Pend.', 'Rate', ...extraCols.map(c => c.header)];
      const bodyRows = rows.map((row) => {
        const rate = approvalRateOf(row);
        const extras = extraCols.map(c => `<td>${c.render(row)}</td>`).join('');
        return `<tr>
          <td>${row._id || 'Unknown'}</td>
          <td>${row.count}</td>
          <td style="color:#27ae60;">${row.approved || 0}</td>
          <td style="color:#c0392b;">${row.rejected || 0}</td>
          <td style="color:#e8a020;">${row.pending || 0}</td>
          <td>${rate === null ? '—' : `${rate}%`}</td>
          ${extras}
        </tr>`;
      }).join('');
      return `<table>
        <thead><tr>${headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>`;
    };

    const rejectionRows = (data?.rejectionReasons || []).map(r => `
      <tr><td>${r._id}</td><td>${r.count}</td></tr>`).join('');

    const trendRows = trend.map(t => `
      <tr>
        <td>${monthLabel(t._id)}</td><td>${t.total}</td>
        <td style="color:#27ae60;">${t.approved}</td>
        <td style="color:#c0392b;">${t.rejected}</td>
        <td style="color:#e8a020;">${t.pending}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8">
    <title>Pre-Shift Request Analytics Report — ${filterSummary}</title>
    <style>
      @page { size: A4 portrait; margin: 15mm 12mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Courier New', monospace; background: #fff; color: #0d1b2a; font-size: 11px; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { padding: 0; }
      .flex-row { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 10px; }
      .card { background: #f2f2f2; border: 1px solid #d0d0d0; border-radius: 8px; padding: 14px; }
      .label { font-size: 9px; letter-spacing: 1.5px; text-transform: uppercase; color: #555; }
      table { width: 100%; border-collapse: collapse; font-size: 10px; }
      th { background: #f0f4f8; padding: 7px 10px; text-align: left; font-weight: 700; letter-spacing: 0.5px; border-bottom: 2px solid #e0e7ef; font-size: 9px; text-transform: uppercase; }
      td { padding: 6px 10px; border-bottom: 1px solid #f0f4f8; }
      tr:last-child td { border-bottom: none; }
      @media print { body { padding: 0; } }
    </style></head>
    <body><div class="page">

      <!-- REPORT HEADER -->
      <div style="background:#0d1b2a;padding:20px 24px;border-radius:10px;margin-bottom:20px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div>
            <div style="font-size:18px;font-weight:700;color:#f0f4f8;letter-spacing:1px;">Pre-Shift Request Analytics Report</div>
            <div style="font-size:10px;color:#0cb89e;margin-top:4px;letter-spacing:2px;">BEAUMONT HOSPITAL · STAFF SCHEDULING SYSTEM</div>
          </div>
          <div style="text-align:right;">
            <div style="font-size:9px;color:#8fa3b1;">Generated</div>
            <div style="font-size:11px;color:#f0f4f8;font-weight:700;">${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}</div>
            <div style="font-size:9px;color:#8fa3b1;margin-top:4px;">By: ${userInfo?.name || 'System'}</div>
          </div>
        </div>
        <div style="margin-top:14px;padding:10px 14px;background:rgba(255,255,255,0.07);border-radius:6px;border-left:3px solid #0a8577;">
          <div style="font-size:9px;color:#8fa3b1;letter-spacing:1px;text-transform:uppercase;margin-bottom:3px;">Filters Applied</div>
          <div style="font-size:10px;color:#f0f4f8;">${filterSummary}</div>
        </div>
        <div style="display:flex;gap:16px;margin-top:12px;">
          <div style="font-size:10px;color:#0cb89e;font-weight:700;">${summary.total} requests</div>
          ${summary.pending > 0 ? `<div style="font-size:10px;color:#e8a020;font-weight:700;">${summary.pending} pending</div>` : ''}
        </div>
      </div>

      <!-- SECTION 1: REQUEST OVERVIEW -->
      ${sectionTitle('1 · Request Overview')}
      <div class="flex-row">
        ${statBox('Total Requests',        summary.total,     null,                              '#0a8577')}
        ${statBox('Pending',               summary.pending,   null,                              '#e8a020')}
        ${statBox('Approved',              summary.approved,  null,                              '#27ae60')}
        ${statBox('Rejected',              summary.rejected,  null,                              '#c0392b')}
        ${statBox('Approval Rate',         summary.approvalRate === null ? '—' : `${summary.approvalRate}%`, 'of decided requests', '#0cb89e')}
        ${statBox('Avg Time to Decision',  formatHours(summary.avgApprovalTimeHours), 'request → decision', '#8e44ad')}
      </div>
      <div class="card" style="margin-bottom:10px;">
        <div class="label" style="margin-bottom:10px;">Status Breakdown</div>
        ${barRow('Approved', summary.approved, summary.total, '#27ae60')}
        ${barRow('Rejected', summary.rejected, summary.total, '#c0392b')}
        ${barRow('Pending',  summary.pending,  summary.total, '#e8a020')}
      </div>

      <!-- SECTION 2: MONTHLY REQUEST VOLUME -->
      ${sectionTitle('2 · Monthly Request Volume')}
      <div class="card" style="margin-bottom:20px;">
        ${trend.length === 0
          ? '<div style="font-size:10px;color:#8fa3b1;">No data for the current filters.</div>'
          : `<table>
              <thead><tr><th>Month</th><th>Total</th><th>Approved</th><th>Rejected</th><th>Pending</th></tr></thead>
              <tbody>${trendRows}</tbody>
            </table>`}
      </div>

      <!-- SECTION 3: SHIFT TYPE ANALYSIS -->
      ${sectionTitle('3 · Shift Type Analysis')}
      <div class="card" style="margin-bottom:10px;">
        <div class="label" style="margin-bottom:10px;">Requests by Reason — outcome breakdown</div>
        ${breakdownTable(data?.byReason || [], 'Reason')}
      </div>
      ${(data?.bySpecialsReason || []).length > 0 ? `
      <div class="card" style="margin-bottom:20px;">
        <div class="label" style="margin-bottom:10px;">1-1 Special / Cohorts — Clinical Reason</div>
        <table>
          <thead><tr><th>Clinical Reason</th><th>Count</th></tr></thead>
          <tbody>${data.bySpecialsReason.map(r => `<tr><td>${r._id || 'Unknown'}</td><td>${r.count}</td></tr>`).join('')}</tbody>
        </table>
      </div>` : ''}

      <!-- SECTION 4: BY STAFF CATEGORY & DIRECTORATE -->
      ${sectionTitle('4 · By Staff Category & Directorate')}
      <div style="display:flex;gap:10px;margin-bottom:20px;">
        <div class="card" style="flex:1;">
          <div class="label" style="margin-bottom:10px;">Requests by Staff Category</div>
          ${(data?.byCategory || []).length === 0
            ? '<div style="font-size:10px;color:#8fa3b1;">No data for the current filters.</div>'
            : `<table>
                <thead><tr><th>Category</th><th>Count</th></tr></thead>
                <tbody>${data.byCategory.map(r => `<tr><td>${r._id || 'Unknown'}</td><td>${r.count}</td></tr>`).join('')}</tbody>
              </table>`}
        </div>
        <div class="card" style="flex:1.4;">
          <div class="label" style="margin-bottom:10px;">Requests by Directorate — outcome breakdown</div>
          ${breakdownTable(data?.byDirectorate || [], 'Directorate')}
        </div>
      </div>

      <!-- SECTION 5: WARDS, REQUESTERS & APPROVERS -->
      ${sectionTitle('5 · Wards, Requesters & Approvers')}
      <div class="card" style="margin-bottom:14px;">
        <div class="label" style="margin-bottom:10px;">Top Wards / Departments (by volume)</div>
        ${breakdownTable(data?.byWard || [], 'Ward', [{ header: 'Directorate', render: (row) => row.directorate || '—' }])}
      </div>
      <div class="card" style="margin-bottom:14px;">
        <div class="label" style="margin-bottom:10px;">Requesters (by volume)</div>
        ${breakdownTable(data?.byRequester || [], 'Requester')}
      </div>
      <div class="card" style="margin-bottom:20px;">
        <div class="label" style="margin-bottom:10px;">Approvers (by decisions made)</div>
        ${breakdownTable(data?.byApprover || [], 'Approver', [{ header: 'Avg Time', render: (row) => formatHours(row.avgApprovalTimeHours) }])}
      </div>

      <!-- SECTION 6: REJECTION REASONS -->
      ${sectionTitle('6 · Most Common Rejection Reasons')}
      <div class="card" style="margin-bottom:24px;">
        ${(data?.rejectionReasons || []).length === 0
          ? '<div style="font-size:10px;color:#8fa3b1;">No rejections recorded for the current filters.</div>'
          : `<table>
              <thead><tr><th>Reason</th><th>Count</th></tr></thead>
              <tbody>${rejectionRows}</tbody>
            </table>`}
      </div>

      <!-- FOOTER -->
      <div style="border-top:2px solid #0d1b2a;padding-top:10px;display:flex;justify-content:space-between;align-items:center;">
        <div style="font-size:8px;color:#8fa3b1;letter-spacing:1px;">BEAUMONT HOSPITAL STAFF SCHEDULING SYSTEM</div>
        <div style="font-size:8px;color:#8fa3b1;">Generated ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()} by ${userInfo?.name || 'System'}</div>
        <div style="font-size:8px;color:#8fa3b1;">beaumontNursingBank@beaumont.ie</div>
      </div>

    </div>
    <script>window.onload=function(){setTimeout(()=>{window.print();setTimeout(()=>window.close(),200);},600);};</script>
    </body></html>`;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: NAVY, p: { xs: 2, md: 3 }, fontFamily: FONT }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;700&display=swap');`}</style>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <IconButton onClick={() => navigate('/admin/ward-preshifts')} size="small" sx={{ color: MUTED, '&:hover': { color: TEAL_L, bgcolor: `${TEAL}22` } }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
              <AssessmentIcon sx={{ color: TEAL, fontSize: 28 }} />
              <Typography sx={{ fontFamily: FONT, fontSize: { xs: '1.3rem', md: '1.7rem' }, fontWeight: 700, color: WHITE, letterSpacing: 1 }}>
                Pre-Shift Request Analytics
              </Typography>
            </Box>
            <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: FONT, letterSpacing: 1.5 }}>
              TRENDS ACROSS REQUESTERS, APPROVERS, DIRECTORATES, WARDS &amp; REASONS
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Chip label={`${summary.total} requests`} size="small" sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, fontFamily: FONT, fontSize: '0.7rem', border: `1px solid ${TEAL}44` }} />
          {summary.pending > 0 && (
            <Chip label={`${summary.pending} pending`} size="small" sx={{ bgcolor: `${AMBER}22`, color: AMBER, fontFamily: FONT, fontSize: '0.7rem', border: `1px solid ${AMBER}44` }} />
          )}
          <Tooltip title="Print report">
            <Button onClick={handlePrint} disabled={isLoading} size="small" startIcon={<PrintIcon sx={{ fontSize: 16 }} />}
              sx={{ bgcolor: `${TEAL}22`, color: TEAL_L, border: `1px solid ${TEAL}44`, fontFamily: FONT, fontSize: '0.72rem', letterSpacing: 1, textTransform: 'uppercase', px: 1.5, py: 0.75, '&:hover': { bgcolor: TEAL, color: WHITE } }}>
              Print Report
            </Button>
          </Tooltip>
          <Tooltip title="Refresh data">
            <IconButton onClick={refetch} disabled={isFetching} size="small" sx={{ color: MUTED, '&:hover': { color: TEAL_L, bgcolor: `${TEAL}22` } }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Filters ── */}
      <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2, mb: 3 }}>
        <Typography sx={{ fontSize: '0.68rem', letterSpacing: 2, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 1.5 }}>Filters</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField size="small" label="From" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED, fontSize: '0.8rem' } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE, '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } }, '& input': { fontFamily: FONT, fontSize: '0.8rem' } }} />
          <TextField size="small" label="To" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true, style: { color: MUTED, fontSize: '0.8rem' } }}
            sx={{ '& .MuiOutlinedInput-root': { bgcolor: NAVY, color: WHITE, '& fieldset': { borderColor: `${TEAL}44` }, '&:hover fieldset': { borderColor: TEAL } }, '& input': { fontFamily: FONT, fontSize: '0.8rem' } }} />
          <FilterSelect label="Directorate" value={fDirectorate} onChange={v => { setFDirectorate(v); setFWard(''); }} options={directorates} />
          <FilterSelect label="Ward / Dept" value={fWard} onChange={setFWard} options={wards} />
          {hasFilters && (
            <Button size="small" onClick={clearAllFilters} startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
              sx={{ color: MUTED, fontFamily: FONT, fontSize: '0.7rem', '&:hover': { color: WHITE } }}>
              Clear filters
            </Button>
          )}
        </Box>
      </Paper>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
          <CircularProgress sx={{ color: TEAL }} />
        </Box>
      ) : isError ? (
        <Alert severity="error" sx={{ fontFamily: FONT }}>
          Failed to load pre-shift analytics: {error?.data?.message || error?.error || 'Unknown error'}
        </Alert>
      ) : (
        <Box>
          {/* ══ Request Overview ══ */}
          <SectionTitle icon={AssessmentIcon} label="Request Overview" />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Total Requests" value={summary.total} icon={AssessmentIcon} color={TEAL} /></Grid>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Pending" value={summary.pending} icon={PendingActionsIcon} color={AMBER} /></Grid>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Approved" value={summary.approved} icon={CheckCircleOutlineIcon} color={GREEN} /></Grid>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Rejected" value={summary.rejected} icon={CancelOutlinedIcon} color={RED} /></Grid>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Approval Rate" value={summary.approvalRate === null ? '—' : `${summary.approvalRate}%`} sub="of decided requests" icon={TrendingUpIcon} color={TEAL_L} /></Grid>
            <Grid item xs={6} sm={4} md={2}><StatCard label="Avg Time to Decision" value={formatHours(summary.avgApprovalTimeHours)} sub="request → decision" icon={AccessTimeIcon} color={PURPLE} /></Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 2, alignSelf: 'flex-start' }}>Approval Rate</Typography>
                <GaugeRing value={summary.approvalRate} label="Of Decided Requests" color={summary.approvalRate >= 70 ? GREEN : summary.approvalRate >= 40 ? AMBER : RED} size={120} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={8}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 2 }}>Status Breakdown</Typography>
                <BarRow label="Approved" value={summary.approved} total={summary.total} color={GREEN} />
                <BarRow label="Rejected" value={summary.rejected} total={summary.total} color={RED} />
                <BarRow label="Pending"  value={summary.pending}  total={summary.total} color={AMBER} />
              </Paper>
            </Grid>
          </Grid>

          {/* ══ Monthly Trend ══ */}
          <SectionTitle icon={TrendingUpIcon} label="Monthly Request Volume" />
          <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, mb: 4 }}>
            {trend.length === 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 280 }}>
                <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: FONT }}>No data for the current filters.</Typography>
              </Box>
            ) : (
              <LineChart
                height={300}
                xAxis={[{ scaleType: 'point', data: trendLabels }]}
                series={[
                  { data: trend.map((t) => t.total),    label: 'Total',    color: OUTCOME_COLORS.total },
                  { data: trend.map((t) => t.approved), label: 'Approved', color: OUTCOME_COLORS.approved },
                  { data: trend.map((t) => t.rejected), label: 'Rejected', color: OUTCOME_COLORS.rejected },
                  { data: trend.map((t) => t.pending),  label: 'Pending',  color: OUTCOME_COLORS.pending },
                ]}
                margin={{ left: 40, right: 20, top: 20, bottom: 40 }}
                sx={chartDarkSx}
              />
            )}
          </Paper>

          {/* ══ Reason + Specials/Cohorts ══ */}
          <SectionTitle icon={LocalHospitalIcon} label="Shift Type Analysis" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={specialsPieData.length > 0 ? 7 : 12}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 2 }}>
                  Requests by Reason — outcome breakdown
                </Typography>
                <StackedOutcomeBarChart rows={data?.byReason || []} height={Math.max(260, (data?.byReason?.length || 0) * 34)} />
              </Paper>
            </Grid>
            {specialsPieData.length > 0 && (
              <Grid item xs={12} md={5}>
                <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid #f4433633`, borderRadius: 2, p: 2.5, height: '100%' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 3, height: 16, bgcolor: '#f44336', borderRadius: 1 }} />
                    <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: '#f44336', fontFamily: FONT }}>
                      1-1 Special / Cohorts — Clinical Reason
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PieChart
                      series={[{ data: specialsPieData, innerRadius: 40, paddingAngle: 1, cornerRadius: 2, cx: '70%' }]}
                      height={280}
                      width={380}
                      margin={{ left: 140, right: 20, top: 20, bottom: 20 }}
                      slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'left' } } }}
                      sx={chartDarkSx}
                    />
                  </Box>
                </Paper>
              </Grid>
            )}
          </Grid>

          {/* ══ Category + Directorate ══ */}
          <SectionTitle icon={GroupIcon} label="By Staff Category & Directorate" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={5}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 2 }}>
                  Requests by Staff Category
                </Typography>
                {categoryPieData.length === 0 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 260 }}>
                    <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: FONT }}>No data for the current filters.</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <PieChart
                      series={[{ data: categoryPieData, innerRadius: 40, paddingAngle: 1, cornerRadius: 2 }]}
                      height={280}
                      width={340}
                      slotProps={{ legend: { direction: 'column', position: { vertical: 'middle', horizontal: 'right' } } }}
                      sx={chartDarkSx}
                    />
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={12} md={7}>
              <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${TEAL}22`, borderRadius: 2, p: 2.5, height: '100%' }}>
                <Typography sx={{ fontSize: '0.7rem', letterSpacing: 1.5, textTransform: 'uppercase', color: MUTED, fontFamily: FONT, mb: 2 }}>
                  Requests by Directorate — outcome breakdown
                </Typography>
                <StackedOutcomeBarChart rows={data?.byDirectorate || []} height={Math.max(260, (data?.byDirectorate?.length || 0) * 34)} />
              </Paper>
            </Grid>
          </Grid>

          {/* ══ Leaderboards ══ */}
          <SectionTitle icon={BusinessIcon} label="Wards, Requesters & Approvers" />
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} md={4}>
              <BreakdownTable
                title="Top Wards / Departments (by volume)"
                rows={data?.byWard || []}
                extraColumns={[{ key: 'directorate', header: 'Directorate', render: (row) => row.directorate || '—' }]}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BreakdownTable title="Requesters (by volume)" rows={data?.byRequester || []} />
            </Grid>
            <Grid item xs={12} md={4}>
              <BreakdownTable
                title="Approvers (by decisions made)"
                rows={data?.byApprover || []}
                extraColumns={[{ key: 'avgTime', header: 'Avg Time', render: (row) => formatHours(row.avgApprovalTimeHours) }]}
                emptyText="No requests have been approved or rejected yet."
              />
            </Grid>
          </Grid>

          {/* ══ Rejection reasons ══ */}
          <SectionTitle icon={ThumbDownAltOutlinedIcon} label="Most Common Rejection Reasons" />
          <Paper elevation={0} sx={{ bgcolor: SLATE, border: `1px solid ${RED}22`, borderRadius: 2, p: 2.5, mb: 2 }}>
            {(data?.rejectionReasons || []).length === 0 ? (
              <Typography sx={{ color: MUTED, fontSize: '0.75rem', fontFamily: FONT }}>No rejections recorded for the current filters.</Typography>
            ) : (
              <Grid container spacing={1.5}>
                {data.rejectionReasons.map((r) => (
                  <Grid item xs={12} sm={6} key={r._id}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1, borderRadius: 1, bgcolor: NAVY, border: `1px solid ${RED}22` }}>
                      <Typography sx={{ fontSize: '0.72rem', color: WHITE, fontFamily: FONT, pr: 1 }}>{r._id}</Typography>
                      <Chip label={r.count} size="small" sx={{ bgcolor: `${RED}22`, color: RED, fontFamily: FONT, fontSize: '0.65rem', border: `1px solid ${RED}44` }} />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            )}
          </Paper>

          {/* Footer */}
          <Box sx={{ textAlign: 'center', pt: 2, pb: 1 }}>
            <Typography sx={{ fontSize: '0.62rem', color: `${MUTED}88`, fontFamily: FONT, letterSpacing: 1 }}>
              BEAUMONT HOSPITAL STAFF SCHEDULING SYSTEM · DATA REFLECTS SELECTED DATE RANGE AND FILTERS · {new Date().toLocaleString()}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default PreShiftAnalyticsDashboard;